import {
	ArrowBack,
	AttachFileOutlined,
	ChatOutlined,
	Print,
} from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import {
	Alert,
	Badge,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	IconButton,
	Modal,
	Tooltip,
	Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import html2pdf from "html2pdf.js";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
	createAddendum,
	deleteESR,
	ESRThunkDispatch,
	fetchESRPermissions,
	fetchESRResponses,
	fetchESRSummary,
	getESRequstInfoReviewSummary,
	getESRReviewSummary,
	listAllUsers,
	RESET_ESR_DETAILS,
	RESET_ESR_DETAILS_COMPLETE,
	RESET_ESR_DETAILS_IN_PROGRESS,
} from "../../actions/form";
import { listMessages } from "../../actions/util";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { DataAction } from "../../types/action";
import { ESRState } from "../../types/data";
import { Avatar } from "../Avatar";
import { Spinner } from "../shared/Spinner";
import { UserSharingSelection } from "../UserSharingSelection";

type QuestionAnswer = {
	question_text?: string;
	response?: string;
};

export const ESRProjectSummary: FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch<ESRThunkDispatch>();
	const { esrId = "", activeLabel = "", activeId = "" } = useParams();
	const [user, setUser] = useCurrentUser();
	const dev_mode = process.env.DEV_MODE || "false";
	const questions = useSelector((state: ESRState) => state.questions);
	const responses = useSelector((state: ESRState) => state.responses);
	const all_responses = useSelector((state: ESRState) => state.all_responses);
	let addednumButtonMessage = "New Addendum"
	if(responses && responses.hasOwnProperty("E25")){
		if(responses["E25"]?.["response"] == "PEL" || responses["E25"]?.["response"] == "NEPA scoping"){
			addednumButtonMessage = "Project sponsor has determined alternative to be carried forward"
		}
	}
	const addendums = useSelector((state: ESRState) => state.addendums);	
	let isUserAllowedToCreateAddednums = true;
	let isUserAllowedToViewOrCreateMessages = true;
	let isUserAllowedToDeleteESR = false;
	if (user?.groups){
		user?.groups?.forEach((g) => {
			if (
				g.endsWith('-Wetlands') || g.endsWith('-Biological Surveys') || g.endsWith('-UIUC') || g.endsWith('-SIU') || g.endsWith('-NIU') ||
				g.startsWith('PRI') || 
				g.startsWith('ISAS') || 
				g.startsWith('ISGS') ||
				g.startsWith('ISWS') ||
				g.startsWith('IDNR')
			) isUserAllowedToCreateAddednums = false;
			if (g === 'IDNR')
				isUserAllowedToViewOrCreateMessages = false;
		});
		user?.groups?.forEach((g) => {
			if (g === 'Admin') {
				isUserAllowedToCreateAddednums = true;
				isUserAllowedToViewOrCreateMessages = true;
				isUserAllowedToDeleteESR = true;
			}
		});
	}
	const review_summary = useSelector((state: ESRState) => state.review_summary);
	const request_info_summary = useSelector((state: ESRState) => state.request_for_more_info);
	const esr_timestamp = useSelector((state: ESRState) => state.esr_timestamp);
	// ESR RESET DETAILS loading state
	const reset_esr_complete = useSelector(
		(state: ESRState) => state.esr_reset.complete,
	);
	const reset_esr_in_progress = useSelector(
		(state: ESRState) => state.esr_reset.inProgress,
	);

	const responder_ids = useSelector((state: ESRState) => state.responder_ids);
	const user_list = useSelector((state: ESRState) => state.user_list);
	const permitted_lists = useSelector((state: ESRState) => state.permissions);
	const is_user_permitted_to_view_esr = useSelector(
		(state: ESRState) => state.is_user_permitted,
	);
	const [fullUserPermissionsList, setFullUserPermissionsList] = useState<
		string[]
	>([]);
	const [fullGroupPermissionsList, setFullGroupPermissionsList] = useState<
		string[]
	>([]);
	const [fullOrgPermissionsList, setFullOrgPermissionsList] = useState<
		string[]
	>([]);
	const [visibleEditors, setVisibleEditors] = useState<string[]>([]);
	const [editorCount, maxEditorCount] = useState(5);

	const threads = useSelector((state: ESRState) => state.threads);
	// const filtered_threads = useSelector(
	// 		(state: ESRState) => state.filtered_threads,
	// 	);
	const unreadMessgCount = useMemo(() => {
		if (threads) {
			return Object.values(threads)
				.filter((thread) =>
					activeLabel === "initial"
						? (thread?.addendum_id === esrId || thread?.esr_id === esrId) &&
							thread?.unread_messages > 0
						: (thread?.esr_id === esrId ||
								thread?.addendum_label === activeLabel) &&
							thread?.unread_messages > 0,
				)
				.reduce((a, b) => a + b.unread_messages, 0);
		}
		return 0;
	}, [threads]);

	const [editorsModal, setEditorsModal] = useState(false);
	const openEditorsModal = () => setEditorsModal(true);
	const closeEditorsModal = () => setEditorsModal(false);

	const [groupsModal, setGroupsModal] = useState(false);
	const openShareUsersModal = () => {
		dispatch(listAllUsers());
		setGroupsModal(true);
	}
	const closeShareUsersModal = () => setGroupsModal(false);

	const setActiveAddendum = (activeLabel: string, activeId: string) => {
		navigate(`/esr/${esrId}/${activeLabel}/${activeId}`);
	};

	useEffect(() => {
		if (user) {
			const initialize = async () => {
				dispatch({ type: RESET_ESR_DETAILS_IN_PROGRESS });
				dispatch({ type: RESET_ESR_DETAILS });
				await Promise.all([
					dispatch(fetchESRSummary(activeId)),
					dispatch(fetchESRResponses(activeId)),
					dispatch(getESRReviewSummary(activeId)),
					dispatch(getESRequstInfoReviewSummary(activeId)),
					dispatch(fetchESRPermissions(activeId)),
					dispatch(listMessages(activeId)),
				]);
				dispatch({ type: RESET_ESR_DETAILS_COMPLETE });
			};
			initialize();
		}
	}, [user, activeId]);

	useEffect(() => {
		if (responder_ids.length > 0) {
			let newVisible: string[] = [];
			responder_ids.forEach((responderEmail) => {
				if (user_list.hasOwnProperty(responderEmail)) {
					const userInfo = user_list[responderEmail];
					const niceLabel = `${userInfo.first_name} ${userInfo.last_name} <${responderEmail}>`;
					newVisible.push(niceLabel);
				} else {
					newVisible.push(responderEmail);
				}
			});
			setVisibleEditors([
				...new Set(newVisible.sort((a, b) => a.localeCompare(b))),
			]);
		}
	}, [user_list, responder_ids]);

	useEffect(() => {
		const permittedUsers: string[] = [];
		const permittedGroups: string[] = [];
		const permittedOrgs: string[] = [];
		if (permitted_lists && permitted_lists.hasOwnProperty("read_only")) {
			const subg = permitted_lists["read_only"];
			if (subg.hasOwnProperty("users")) {
				subg["users"].forEach((responderEmail: string) => {
					permittedUsers.push(fetchNiceLabel(responderEmail));
				});
			}
			subg["groups"].forEach((groupName: string) => {
				permittedGroups.push(groupName);
			});
			if (subg.hasOwnProperty("organizations")) {
				subg["organizations"].forEach((orgName: string) => {
					permittedOrgs.push(orgName);
				});
			}
		}
		if (permitted_lists && permitted_lists.hasOwnProperty("editor")) {
			const subg = permitted_lists["editor"];
			if (subg.hasOwnProperty("users")) {
				subg["users"].forEach((responderEmail: string) => {
					permittedUsers.push(fetchNiceLabel(responderEmail));
				});
			}
			if (subg.hasOwnProperty("groups")) {
				subg["groups"].forEach((groupName: string) => {
					permittedGroups.push(groupName);
				});
			}
		}
		const uniqueUsers = [...new Set(permittedUsers)];
		setFullUserPermissionsList(uniqueUsers.sort((a, b) => a.localeCompare(b)));
		setFullGroupPermissionsList(
			permittedGroups.sort((a, b) => a.localeCompare(b)),
		);
		setFullOrgPermissionsList(permittedOrgs.sort((a,b) => a.localeCompare(b)));
	}, [permitted_lists]);

	const fetchNiceLabel = (email: string) => {
		if (user_list.hasOwnProperty(email)) {
			const userInfo = user_list[email];
			const niceLabel = `${userInfo.first_name} ${userInfo.last_name} <${email}>`;
			return niceLabel;
		} else {
			return email;
		}
	};

	const viewESR = () => {
		navigate(`/esr/${esrId}/${activeLabel}/${activeId}/0`);
	};

	const createNewAddendum = () => {
		if (user != undefined) dispatch(createAddendum(user.email, esrId));
	};
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const deleteESRHandle = async () => {
		try {
			setDeleting(true);
			await dispatch(deleteESR(activeId));
			navigate("/all");
		} catch (error) {
			console.error(error);
			alert("Failed to delete ESR");
		} finally {
			setDeleting(false);
			setDeleteDialogOpen(false);
		}
	};
	const request_info_from_sponsor: boolean = useSelector(
		(state: ESRState) => state.request_info_from_sponsor,
	);
	const openThreads = () => {
		dispatch<DataAction>({
			type: "UPDATE_DRAWER_STATE",
			update: {
				attachmentsOpen: false,
				threadsOpen: true,
				selectedThreadId: -1,
			},
		});
		if (request_info_from_sponsor) {
			dispatch<DataAction>({
				type: "REQUEST_INFO_FROM_SPONSOR_COMPLETE",
			});
		}
	};

	const openAttachments = () => {
		dispatch<DataAction>({
			type: "UPDATE_DRAWER_STATE",
			update: {
				attachmentsOpen: true,
				threadsOpen: false,
			},
		});
	};

	const openEditors = () => {
		if (activeId && activeId !== "") dispatch(fetchESRPermissions(activeId));
		setEditorsModal(true);
	};

	const contentRef = useRef<HTMLDivElement | null>(null);
	const handleGeneratePdf = async (excludeList: string[]) => {
		let questionAnswerList: Record<string, QuestionAnswer> = {};
		if (responses && questions) {
			questions?.groups?.forEach((group) =>
				group?.subgroups?.forEach((subgroup) => {
					if (
						subgroup?.name?.includes("1.1. Project Information") ||
						subgroup?.name?.includes("2.2. Location Description")
					) {
						subgroup.questions?.forEach(
							(question) =>
								(questionAnswerList[question["question_id"]] = {
									question_text: question["question_text"],
									response: responses[question["question_id"]]?.response,
								}),
						);
					}
				}),
			);
		}
		if (!contentRef.current) return;
		const filename = `project_summary_ESR_ID_${esrId}${activeLabel !== "initial" ? activeLabel : ""}.pdf`;
		const opt = {
			margin: 10,
			filename,
			image: { type: "jpeg", quality: 0.98 },
			html2canvas: {
				ignoreElements: (element) => {
					return excludeList.some((cls) => element.classList?.contains(cls));
				},
				scale: 2,
				scrollY: 0,
			},
			jsPDF: {
				orientation: "p",
				unit: "mm",
				format: "a4",
				putOnlyUsedFonts: true,
				compress: true,
				floatPrecision: 16,
			},
		};
		// Display the question answers by injecting it temporarily
		if (Object.entries(questionAnswerList).length > 0) {
			const clonedContent = contentRef.current.cloneNode(true);
			const extraSection = document.createElement("div");
			extraSection.innerHTML = `
					<hr style="margin:20px 0;" />
					<h2 style="
						font-size:20px; 
						margin-bottom:10px; 
						text-align:center; 
						color:#2E3B55;
					">
						Project Information
					</h2>
					<table style="
						width:100%;
						border-collapse:collapse;
						border:1px solid #ccc;
						font-size:14px;
						font-family:Arial, sans-serif;
					">
						<tbody>
						${Object.entries(questionAnswerList)
							.map(
								([key, value], index) => `
								<tr 
									style="background-color:${index % 2 === 0 ? "#ffffff" : "#f9f9f9"};
									page-break-inside:avoid;
								">
								<td style="border:1px solid #ddd; padding:8px; font-weight:500;">
									${value?.question_text ?? key}
								</td>
								<td style="border:1px solid #ddd; padding:8px;">
									${value?.response || ""}
								</td>
								</tr>`,
							)
							.join("")}
						</tbody>
					</table>
					<div style="
						margin-top:30px;
						border-top:1px solid #ccc;
						padding-top:8px;
						text-align:right;
						font-size:12px;
						color:#555;
						font-style:italic;
					">
						Generated on: ${new Date().toLocaleString()}
					</div>
			`;
			clonedContent.appendChild(extraSection);
			const hiddenContainer = document.createElement("div");
			hiddenContainer.style.position = "absolute";
			hiddenContainer.style.left = "-9999px";
			hiddenContainer.appendChild(clonedContent);
			document.body.appendChild(hiddenContainer);
			html2pdf().set(opt).from(clonedContent).save();
			document.body.removeChild(hiddenContainer);
		}
	};

	return (
		<>
			{reset_esr_complete && (addendums || user?.admin) && (
				<Box sx={{ height: "100%", overflow: "scroll" }} key={esrId}>
					<Box
						sx={{
							position: "absolute",
							left: 0,
							right: 0,
							background: "#003E6205",
							// height: 374,
							height: 374 - 230,
							zIndex: -1,
						}}
					/>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							alignItems: "stretch",
							mb: "100px",
						}}
					>
						{dev_mode == "true" && Number(esrId) >= 999000000 ? (
							<>
								<Box sx={{ flex: 1 }}>
									<Button
										className="no-print"
										sx={{
											m: "32px",
											fontSize: "16px",
											color: "#00000099",
											textTransform: "capitalize",
										}}
										variant="text"
										startIcon={<ArrowBack sx={{ color: "#003E628A" }} />}
										onClick={() => navigate("/test")}
									>
										Test Projects
									</Button>
								</Box>
							</>
						) : (
							<>
								<Box sx={{ flex: 1 }}>
									<Button
										className="no-print"
										sx={{
											m: "32px",
											fontSize: "16px",
											color: "#00000099",
											textTransform: "capitalize",
										}}
										variant="text"
										startIcon={<ArrowBack sx={{ color: "#003E628A" }} />}
										onClick={() => navigate("/all")}
									>
										All Projects
									</Button>
								</Box>
							</>
						)}
						<Box
							sx={{
								flex: 4,
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box
								sx={{
									height: 142,
									boxSizing: "border-box",
									pt: "40px",
									pb: "30px",
									display: "flex",
									flexDirection: "row",
									gap: "24px",
								}}
							>
								<Box sx={{ width: 240 }}>
									<Button
										variant="contained"
										size="large"
										fullWidth
										onClick={viewESR}
									>
										View ESR Details
									</Button>
								</Box>
								<Divider orientation="vertical" flexItem />
								<Box sx={{ width: 128 }}>
									<Typography fontWeight={700} fontSize={16}>
										Created Time
									</Typography>
									<Typography
										sx={{
											fontWeight: 400,
											fontSize: 14,
											color: "#00000099",
											my: "12px",
										}}
									>
										{esr_timestamp.substring(0, 10)}
									</Typography>
								</Box>
								<Box sx={{ width: 256 }}>
									<Typography fontWeight={700} fontSize={16}>
										Editors
									</Typography>
									<Box
										sx={{
											display: "flex",
											flexDirection: "row",
											my: "12px",
											position: "relative",
											zIndex: 0,
										}}
									>
										{visibleEditors.slice(0, editorCount).map((color, i) => (
											<Avatar
												key={i}
												name={visibleEditors[i]}
												color="darkblue"
												sx={{ ml: "-8px", zIndex: -i }}
											/>
										))}
										<Avatar
											key="seemore"
											name="..."
											color="#333333"
											sx={{ zIndex: -editorCount }}
											handleOnClick={openEditors}
										/>
										<Button
											variant="contained"
											color="primary"
											size="small"
											onClick={openShareUsersModal}
										>
											Share
										</Button>
										<Box sx={{ width: "5px" }} />
									</Box>
								</Box>

								{/*Editor modal*/}
								<Modal open={editorsModal} onClose={closeEditorsModal}>
									<Paper
										elevation={6}
										sx={{
											position: "absolute",
											top: "50%",
											left: "50%",
											transform: "translate(-50%, -50%)",
											background: "white",
											transition: "all 200ms ease-in-out",
											padding: "2em",
											width: "40em",
										}}
									>
										<Box
											sx={{
												display: "flex",
												height: "30em",
												flexDirection: "column",
												overflow: "hidden",
												overflowY: "scroll",
											}}
										>
											<Typography fontSize={14} fontWeight={500}>
												People who have edited ESR
											</Typography>
											{visibleEditors.map((color, i) => (
												<li key={i}>{visibleEditors[i]}</li>
											))}
											<br />
											<Typography fontSize={14} fontWeight={500}>
												Users with access to ESR
											</Typography>
											{fullUserPermissionsList.map((p) => (
												<li key={p}>{p}</li>
											))}
											<br />
											<Typography fontSize={14} fontWeight={500}>
												Groups with access to ESR
											</Typography>
											{fullGroupPermissionsList.map((p) => (
												<li key={p}>{p}</li>
											))}
											<br />
											<Typography fontSize={14} fontWeight={500}>
												Organizations that can view ESR
											</Typography>
											{fullOrgPermissionsList.map((p) => (
												<li key={p}>{p}</li>
											))}
											<br/>
											<Button onClick={closeEditorsModal}>Close</Button>
										</Box>
									</Paper>
								</Modal>

								<UserSharingSelection
									esrId={esrId}
									isOpen={groupsModal}
									onClose={closeShareUsersModal}
								/>
								<Box sx={{ flex: 1 }} />
							</Box>
							<Divider />
							<Divider />

							<Box ref={contentRef}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 2,
									}}
								>
									<Typography fontWeight={700} fontSize={36}>
										Project Status: ESR #{esrId}
										{activeLabel === "initial" ? "" : activeLabel}
									</Typography>
									{review_summary &&
										"hidden" in review_summary &&
										review_summary["hidden"] && (
											<Typography fontSize={30} color="error">
												(Deleted Project)
											</Typography>
										)}
									{isUserAllowedToDeleteESR &&
										review_summary &&
										"hidden" in review_summary &&
										!review_summary["hidden"] && (
											<Button
												variant="contained"
												color="error"
												onClick={() => setDeleteDialogOpen(true)}
											>
												Delete ESR
											</Button>
										)}
								</Box>
								{addendums != null && Object.keys(addendums).length > 1 ? (
									<>
										<Box
											sx={{
												border: "1px solid #0000001F",
												p: "16px 16px",
												display: "wrap",
												flexDirection: "row",
												alignItems: "center",
												justifyContent: "space-between",
											}}
										>
											<Button variant="text" disabled={true}>
												<b>NOW VIEWING</b>
											</Button>
											{activeLabel !== "initial" ? (
												<>
													<Button
														disabled={
															"is_user_authorized" in addendums["initial"] &&
															addendums["initial"]["is_user_authorized"]
																? false
																: true
														}
														variant="text"
														onClick={() => setActiveAddendum("initial", esrId)}
													>
														<span
															style={
																addendums["initial"]?.["hidden"]
																	? {
																			color: "red",
																			textDecoration: "line-through",
																			fontWeight: "bold",
																		}
																	: {}
															}
														>
															#{esrId}
														</span>
													</Button>
												</>
											) : (
												<>
													<Button
														disabled={
															"is_user_authorized" in addendums["initial"] &&
															addendums["initial"]["is_user_authorized"]
																? false
																: true
														}
														variant="contained"
														onClick={() => setActiveAddendum("initial", esrId)}
													>
														<span
															style={
																addendums["initial"]?.["hidden"]
																	? {
																			color: "red",
																			textDecoration: "line-through",
																			fontWeight: "bold",
																		}
																	: {}
															}
														>
															#{esrId}
														</span>
													</Button>
												</>
											)}

											{Object.keys(addendums)
												.filter((label) => label !== "initial")
												.sort()
												.map(function (keyName, keyIndex) {
													return (
														<a key={keyName}>
															{activeLabel === keyName ? (
																<>
																	<Button
																		disabled={
																			"is_user_authorized" in
																				addendums[keyName] &&
																			addendums[keyName]["is_user_authorized"]
																				? false
																				: true
																		}
																		variant="contained"
																		style={{ maxWidth: "20px" }}
																		onClick={() => {
																			setActiveAddendum(
																				keyName,
																				addendums[keyName].id,
																			);
																		}}
																	>
																		<span
																			style={
																				addendums[keyName]?.["hidden"]
																					? {
																							color: "red",
																							textDecoration: "line-through",
																							fontWeight: "bold",
																						}
																					: {}
																			}
																		>
																			{keyName}
																		</span>
																	</Button>
																</>
															) : (
																<>
																	<Button
																		disabled={
																			"is_user_authorized" in
																				addendums[keyName] &&
																			addendums[keyName]["is_user_authorized"]
																				? false
																				: true
																		}
																		variant="text"
																		style={{ maxWidth: "20px" }}
																		onClick={() => {
																			setActiveAddendum(
																				keyName,
																				addendums[keyName].id,
																			);
																		}}
																	>
																		<span
																			style={
																				addendums[keyName]?.["hidden"]
																					? {
																							color: "red",
																							textDecoration: "line-through",
																							fontWeight: "bold",
																						}
																					: {}
																			}
																		>
																			{keyName}
																		</span>
																	</Button>
																</>
															)}
														</a>
													);
												})}
										</Box>
									</>
								) : (
									<></>
								)}
								<br />
								{review_summary && review_summary?.is_esr_submitted ? (
									<>
										This project has been <b>submitted to BDE</b>.
									</>
								) : (
									<>
										{!responses.hasOwnProperty("E3") &&
										!responses.hasOwnProperty("E3F") &&
										!responses.hasOwnProperty("E5") ? (
											<>
												This project is a <b>draft</b>, and has not been
												submitted to BDE yet.
											</>
										) : null}
										{responses.hasOwnProperty("E3") &&
										responses["E3"]?.["response"] &&
										!responses.hasOwnProperty("E3F") &&
										!responses.hasOwnProperty("E5") ? (
											<>
												This project has been submitted to District{" "}
												{responses["E9"]?.["response"]}.
											</>
										) : null}
										{responses.hasOwnProperty("E3F") &&
										responses["E3F"]?.["response"] &&
										!responses.hasOwnProperty("E43A") ? (
											<>
												This project has been submitted to District{" "}
												{responses["E9"]?.["response"]} for funding.
											</>
										) : null}
										{responses.hasOwnProperty("E5") &&
										responses["E5"]?.["response"] ? (
											<>This project has been submitted to BDE.</>
										) : null}
									</>
								)}
								<br />

								{review_summary &&
								review_summary?.project_description?.length > 0 ? (
									<>
										<Box
											className="no-print"
											sx={{
												mt: "20px",
												display: "flex",
												flexDirection: "row",
												alignItems: "center",
												whiteSpace: "pre-wrap",
											}}
										>
											<br />
											<b>Description:</b>&nbsp;
											{review_summary?.project_description}
										</Box>
										<br />
									</>
								) : (
									<>
										<br />
									</>
								)}

								{review_summary && review_summary?.is_esr_submitted ? (
									<>
										<Box
											sx={{
												mt: "5px",
												display: "flex",
												gap: 1,
												flexDirection: "row",
											}}
										>
											<Box
												className="no-print"
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
													flexWrap: "wrap",
												}}
											>
												<Button
													className="no-print"
													sx={{ ml: "10px" }}
													startIcon={<Print />}
													variant="outlined"
													onClick={() => handleGeneratePdf(["no-print"])}
												>
													Print / Download
												</Button>
												<Button
													onClick={openAttachments}
													variant="outlined"
													startIcon={<AttachFileOutlined />}
												>
													Project Files
												</Button>

												<Button
													onClick={openThreads}
													variant="outlined"
													disabled={!isUserAllowedToViewOrCreateMessages}
												>
													<Badge badgeContent={unreadMessgCount} color="error">
														<ChatOutlined />
													</Badge>
													<Box ml={1}>Messages</Box>
												</Button>

												<Button
													variant="contained"
													color="primary"
													onClick={createNewAddendum}
													disabled={!isUserAllowedToCreateAddednums}
													sx={{
														ml: "auto",
														whiteSpace: "normal",
														textAlign: "center",
														maxWidth: 300,
														// minHeight: 56,
													}}
												>
													{addednumButtonMessage}
												</Button>
											</Box>
										</Box>
									</>
								) : (
									<></>
								)}

								{review_summary && review_summary?.is_esr_submitted ? (
									<Box mt="20px">
										{review_summary ? (
											<>
												<h2>
													Phase I/Design Approval
													<Tooltip
														arrow
														title={
															<Box>
																<Typography
																	sx={{ color: "green", fontSize: "1em" }}
																>
																	Green - Cleared
																</Typography>
																<Typography
																	sx={{ color: "orange", fontSize: "1em" }}
																>
																	Orange - In progress
																</Typography>
																<Typography
																	sx={{ color: "red", fontSize: "1em" }}
																>
																	Red - Action needed
																</Typography>
															</Box>
														}
														componentsProps={{
															tooltip: {
																sx: {
																	bgcolor: "#fff",
																	color: "inherit",
																	maxWidth: 300,
																	padding: 2,
																	border: "1px solid rgba(0,0,0,0.12)",
																	boxShadow: 3, // Material UI shadow scale (1-24)
																	borderRadius: 2,
																},
															},
														}}
													>
														<IconButton size="small">
															<InfoIcon fontSize="small" color="primary" />
														</IconButton>
													</Tooltip>
												</h2>

												{review_summary["cultural_I"] ? (
													<>
														{!(
															review_summary?.["cultural_I"] &&
															review_summary["cultural_I"].hasOwnProperty(
																"archeology",
															) &&
															review_summary["cultural_I"].hasOwnProperty(
																"historic_architecture",
															)
														) && (
															<>
																<b>Cultural</b>
																<br />
																<span
																	style={{
																		color:
																			review_summary["cultural_I"]?.["color"],
																	}}
																>
																	{review_summary["cultural_I"]?.linked ? (
																		<>
																			<Link
																				to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${review_summary["cultural_I"]?.["link_items"][1]}`}
																				style={{
																					color:
																						review_summary["cultural_I"]?.[
																							"color"
																						],
																					textDecoration: "underline",
																				}}
																			>
																				{review_summary["cultural_I"]?.["text"]}
																			</Link>
																		</>
																	) : (
																		review_summary["cultural_I"]?.["text"]
																	)}
																	{/* {review_summary["cultural_I"]?.["text"]} */}
																</span>
															</>
														)}
														{review_summary["cultural_I"]?.["archeology"] && (
															<>
																<br />
																<b>Cultural - Archaeology</b>
																<br />
																<span
																	style={{
																		color:
																			review_summary["cultural_I"][
																				"archeology"
																			]?.["color"],
																	}}
																>
																	{review_summary["cultural_I"]["archeology"]
																		?.linked ? (
																		<>
																			<Link
																				to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${review_summary["cultural_I"]["archeology"]?.["link_items"][1]}`}
																				style={{
																					color:
																						review_summary["cultural_I"][
																							"archeology"
																						]?.["color"],
																					textDecoration: "underline",
																				}}
																			>
																				{
																					review_summary["cultural_I"][
																						"archeology"
																					]?.["text"]
																				}
																			</Link>
																		</>
																	) : (
																		review_summary["cultural_I"][
																			"archeology"
																		]?.["text"]
																	)}
																	{/* {
																		review_summary["cultural_I"][
																			"archeology"
																		]?.["text"]
																	} */}
																</span>
															</>
														)}
														{request_info_summary["cultural"] ? (
															<>
																{request_info_summary &&
																	review_summary?.is_esr_submitted &&
																	request_info_summary["cultural"]?.[
																		"archeology"
																	] && (
																		<>
																			<br />
																			<b>
																				Request for more information -
																				Archaeology
																			</b>
																			<br />
																			<span
																				style={{
																					color:
																						request_info_summary["cultural"][
																							"archeology"
																						]?.["color"],
																				}}
																			>
																				{request_info_summary["cultural"][
																					"archeology"
																				]?.linked ? (
																					<>
																						<Link
																							to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${request_info_summary["cultural"]["archeology"]?.["link_items"][1]}`}
																							style={{
																								color:
																									request_info_summary[
																										"cultural"
																									]["archeology"]?.["color"],
																								textDecoration: "underline",
																							}}
																						>
																							{
																								request_info_summary[
																									"cultural"
																								]["archeology"]?.["text"]
																							}
																						</Link>
																					</>
																				) : (
																					request_info_summary["cultural"][
																						"archeology"
																					]?.["text"]
																				)}
																			</span>
																		</>
																	)}
															</>
														) : (
															<></>
														)}
														{review_summary["cultural_I"]?.[
															"historic_architecture"
														] && (
															<>
																<br />
																<b>Cultural - Historic Architecture </b>
																<br />
																<span
																	style={{
																		color:
																			review_summary["cultural_I"][
																				"historic_architecture"
																			]?.["color"],
																	}}
																>
																	{/* {
																		review_summary["cultural_I"][
																			"historic_architecture"
																		]?.["text"]
																	} */}
																	{review_summary["cultural_I"][
																		"historic_architecture"
																	]?.linked ? (
																		<>
																			<Link
																				to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${review_summary["cultural_I"]["historic_architecture"]?.["link_items"][1]}`}
																				style={{
																					color:
																						review_summary["cultural_I"][
																							"historic_architecture"
																						]?.["color"],
																					textDecoration: "underline",
																				}}
																			>
																				{
																					review_summary["cultural_I"][
																						"historic_architecture"
																					]?.["text"]
																				}
																			</Link>
																		</>
																	) : (
																		review_summary["cultural_I"][
																			"historic_architecture"
																		]?.["text"]
																	)}
																</span>
															</>
														)}
														<br />
														{request_info_summary["cultural"] ? (
															<>
																{request_info_summary &&
																	review_summary?.is_esr_submitted &&
																	request_info_summary["cultural"]?.[
																		"historic_architecture"
																	] && (
																		<>
																			<br />
																			<b>
																				Request for more information - Historic
																				Architecture{" "}
																			</b>
																			<br />
																			<span
																				style={{
																					color:
																						request_info_summary["cultural"][
																							"historic_architecture"
																						]?.["color"],
																				}}
																			>
																				{request_info_summary["cultural"][
																					"historic_architecture"
																				]?.linked ? (
																					<>
																						<Link
																							to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${request_info_summary["cultural"]["historic_architecture"]?.["link_items"][1]}`}
																							style={{
																								color:
																									request_info_summary[
																										"cultural"
																									]["historic_architecture"]?.[
																										"color"
																									],
																								textDecoration: "underline",
																							}}
																						>
																							{
																								request_info_summary[
																									"cultural"
																								]["historic_architecture"]?.[
																									"text"
																								]
																							}
																						</Link>
																					</>
																				) : (
																					request_info_summary["cultural"][
																						"historic_architecture"
																					]?.["text"]
																				)}
																			</span>
																		</>
																	)}
																<br />
															</>
														) : (
															<></>
														)}
													</>
												) : (
													<></>
												)}

												{review_summary["natural_I"] ? (
													<>
														<b>Natural Resources</b>
														<br />
														<span
															style={{
																color: review_summary["natural_I"]?.["color"],
															}}
														>
															{review_summary["natural_I"]?.linked ? (
																<>
																	<Link
																		to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${review_summary["natural_I"]?.["link_items"][1]}`}
																		style={{
																			color:
																				review_summary["natural_I"]?.["color"],
																			textDecoration: "underline",
																		}}
																	>
																		{review_summary["natural_I"]?.["text"]}
																	</Link>
																</>
															) : (
																review_summary["natural_I"]?.["text"]
															)}
															{/* {review_summary["natural_I"]?.["text"]} */}
														</span>
														<br />
													</>
												) : (
													<></>
												)}
												{request_info_summary &&
												review_summary?.is_esr_submitted &&
												request_info_summary["natural"] ? (
													<>
														<br />
														<b>
															Request for more information - Natural Resources
														</b>
														<br />
														<span
															style={{
																color:
																	request_info_summary["natural"]?.["color"],
															}}
														>
															{request_info_summary["natural"]?.linked ? (
																<>
																	<Link
																		to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${request_info_summary["natural"]?.["link_items"][1]}`}
																		style={{
																			color:
																				request_info_summary["natural"]?.[
																					"color"
																				],
																			textDecoration: "underline",
																		}}
																	>
																		{request_info_summary["natural"]?.["text"]}
																	</Link>
																</>
															) : (
																request_info_summary["natural"]?.["text"]
															)}
														</span>
														<br />
													</>
												) : (
													<></>
												)}
												{review_summary["regulated_I"] ? (
													<>
														<b>Regulated Substances (state ROW only)</b>
														<br />
														<span
															style={{
																color: review_summary["regulated_I"]?.["color"],
															}}
														>
															{review_summary["regulated_I"]?.linked ? (
																<>
																	<Link
																		to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${review_summary["regulated_I"]?.["link_items"][1]}`}
																		style={{
																			color:
																				review_summary["regulated_I"]?.[
																					"color"
																				],
																			textDecoration: "underline",
																		}}
																	>
																		{review_summary["regulated_I"]?.["text"]}
																	</Link>
																</>
															) : (
																review_summary["regulated_I"]?.["text"]
															)}
															{/*  {review_summary["regulated_I"]?.["text"]} */}
														</span>
														<br />
													</>
												) : (
													<></>
												)}
												{request_info_summary &&
												review_summary?.is_esr_submitted &&
												request_info_summary["regulated"] ? (
													<>
														<br />
														<b>
															Request for more information - Regulated
															Substances
														</b>
														<br />
														<span
															style={{
																color:
																	request_info_summary["regulated"]?.["color"],
															}}
														>
															{request_info_summary["regulated"]?.linked ? (
																<>
																	<Link
																		to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${request_info_summary["regulated"]?.["link_items"][1]}`}
																		style={{
																			color:
																				request_info_summary["regulated"]?.[
																					"color"
																				],
																			textDecoration: "underline",
																		}}
																	>
																		{
																			request_info_summary["regulated"]?.[
																				"text"
																			]
																		}
																	</Link>
																</>
															) : (
																request_info_summary["regulated"]?.["text"]
															)}
														</span>
														<br />
													</>
												) : (
													<></>
												)}
												<h2>Phase II/Letting</h2>

												{!(
													review_summary?.["cultural_II"] &&
													Object.hasOwn(
														review_summary["cultural_II"],
														"archeology",
													) &&
													Object.hasOwn(
														review_summary["cultural_II"],
														"historic_architecture",
													)
												) && (
													<>
														<b>Cultural</b>
														<br />
														<span
															style={{
																color: review_summary["cultural_II"]?.["color"],
															}}
														>
															{review_summary["cultural_II"]?.["text"]}
														</span>
													</>
												)}

												{review_summary["cultural_II"] ? (
													<>
														{review_summary["cultural_II"]?.["archeology"] && (
															<>
																<br />
																<b>Cultural - Archaeology</b>
																<br />
																<span
																	style={{
																		color:
																			review_summary["cultural_II"][
																				"archeology"
																			]?.["color"],
																	}}
																>
																	{
																		review_summary["cultural_II"][
																			"archeology"
																		]?.["text"]
																	}
																</span>
																<br />
															</>
														)}

														{review_summary["cultural_II"]?.[
															"historic_architecture"
														] && (
															<>
																<br />
																<b>Cultural - Historic Architecture</b>
																<br />
																<span
																	style={{
																		color:
																			review_summary["cultural_II"][
																				"historic_architecture"
																			]?.["color"],
																	}}
																>
																	Historic architecture{" "}
																	{
																		review_summary["cultural_II"][
																			"historic_architecture"
																		]?.["text"]
																	}
																</span>
															</>
														)}
														<br />
													</>
												) : (
													<></>
												)}
												{review_summary["natural_II"] ? (
													<>
														<b>Natural Resources - Wetlands</b>
														<br />
														<span
															style={{
																color:
																	review_summary["natural_II"]?.["wetlands"]?.[
																		"color"
																	],
															}}
														>
															{
																review_summary["natural_II"]["wetlands"]?.[
																	"text"
																]
															}
														</span>
														<br />
														<b>Natural Resources - Review Memo</b>
														<br />
														<span
															style={{
																color:
																	review_summary["natural_II"][
																		"non_wetlands"
																	]?.["color"],
															}}
														>
															{review_summary["natural_II"]["non_wetlands"]
																?.linked ? (
																<>
																	<Link
																		to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${
																			review_summary["natural_II"][
																				"non_wetlands"
																			]?.["link_items"][1]
																		}`}
																		style={{
																			color:
																				review_summary["natural_II"][
																					"non_wetlands"
																				]?.["color"],
																			textDecoration: "underline",
																		}}
																	>
																		{
																			review_summary["natural_II"][
																				"non_wetlands"
																			]?.["text"]
																		}
																	</Link>
																</>
															) : (
																review_summary["natural_II"]["non_wetlands"]?.[
																	"text"
																]
															)}
														</span>
														<br />
													</>
												) : (
													<></>
												)}
												{review_summary["regulated_II"] ? (
													<>
														<b>Regulated Substances (state ROW only)</b>
														<br />
														<span
															style={{
																color:
																	review_summary["regulated_II"]?.["color"],
															}}
														>
															{review_summary["regulated_II"]?.linked ? (
																<>
																	<Link
																		to={`/esr/${esrId}/${activeLabel}/${activeId}/redirect/${review_summary["regulated_II"]?.["link_items"][1]}`}
																		style={{
																			color:
																				review_summary["regulated_II"]?.[
																					"color"
																				],
																			textDecoration: "underline",
																		}}
																	>
																		{review_summary["regulated_II"]?.["text"]}
																	</Link>
																</>
															) : (
																review_summary["regulated_II"]?.["text"]
															)}
														</span>
														<br />
													</>
												) : (
													<></>
												)}
											</>
										) : (
											<></>
										)}
									</Box>
								) : (
									<></>
								)}
							</Box>
						</Box>
						<Box sx={{ flex: 1 }}></Box>
					</Box>
				</Box>
			)}

			{!(is_user_permitted_to_view_esr || user?.admin) && (
				<Box>
					<Alert variant="filled" severity="error">
						You are not permitted to view ESR# {esrId}.
					</Alert>
				</Box>
			)}
			{reset_esr_in_progress && <Spinner />}
			<Dialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
			>
				<DialogTitle>Delete ESR</DialogTitle>

				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete ESR ? This action cannot be undone.
					</DialogContentText>
				</DialogContent>

				<DialogActions>
					<Button
						onClick={() => setDeleteDialogOpen(false)}
						disabled={deleting}
					>
						Cancel
					</Button>

					<Button
						color="error"
						variant="contained"
						onClick={deleteESRHandle}
						disabled={deleting}
					>
						{deleting ? "Deleting..." : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
